import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideRouter, UrlSerializer } from '@angular/router';
import { provideClientHydration, withEventReplay, withIncrementalHydration } from '@angular/platform-browser';
import { provideServiceWorker } from '@angular/service-worker';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';
import { provideGeolocation } from '@wdpr/ra-angular-geolocation';
import { LoggerService, LogasaurusService, LOGASAURUS_URL, MAX_STRING_LENGTH, WindowRef as WindowRefLoggerHandler } from '@wdpr/ra-angular-logger';
import { SeoService, I18nService, SeoDataService, WindowRef as WindowRefSeoHandler, SEO_OPTIONS } from '@wdpr/ra-angular-seo-metadata';
import { CORRELATION_CONFIG, CorrelationInterceptor, CorrelationService } from '@wdpr/ra-angular-correlation';
import { DeviceDataService, DeviceDetectionConfig, DeviceDetectionOptions } from '@wdpr/ra-angular-device-detection';
import { ErrorService, GlobalExceptionHandlingService, HttpInterceptorService, WindowRef as WindowRefErrorHandler } from '@wdpr/ra-angular-error-handler';
import { CdnService } from '@wdpr/ra-angular-cdn';
import { AuthInterceptor as MyIdAuthInterceptor, RaAngularMyidLoginService } from '@wdpr/ra-angular-myid-login';
import { PAGE_KEY_ADDITIONAL_CONFIG, PAGE_KEY_TEMPLATE, PAGE_KEY_URL_ENDPOINT, PageKeyServerDataService, PageKeyService } from '@wdpr/ra-angular-page-key';
import { ClientIntegration } from '@wdpr/profile-authenticator-universal';

import { CustomUrlSerializer } from '@finder/shared/utils/url/customUrlSerializer';
import { ConfigService } from '@finder/core/config.service';
import { SITE_ID, getSeoOptions } from '@finder/shared/constants/app.constants';
import { LocaleInterceptor } from '@finder/shared/interceptors/locale/locale-interceptor.service';
import { TranslationInterceptor } from '@finder/shared/interceptors/translation/translation-interceptor.service';
import { SiteIdInterceptor } from '@finder/shared/interceptors/site-id/site-id-interceptor.service';
import { HeaderOverrideInterceptor } from '@finder/shared/interceptors/header-overrides-interceptor/header-overrides.interceptor';
import { CustomDisneyHeadersInterceptor } from '@finder/shared/interceptors/custom-disney-headers/custom-disney-headers.interceptor';
import { MyIdAuthSsrInterceptor } from '@finder/shared/interceptors/my-id-auth/my-id-auth-ssr.interceptor';
import { SsrTokensInterceptor } from '@finder/shared/interceptors/ssr-tokens/ssr-tokens-interceptor.service';
import { OneIdService } from '@finder/shared/services/one-id/one-id.service';
import { OneIdFactory } from '@finder/shared/services/one-id/one-id.factory';
import { WindowRef as LocalWindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { APPLICATION_ROUTES } from '@finder/app.routes';
import { geoConfig } from '@finder/common/geo-config.constants';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { HeaderOverridesService } from '@finder/shared/services/header-overrides/header-overrides.service';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { SsrService } from '@finder/shared/services/ssr/ssr.service';
import { DebugService } from '@finder/features/debug/services/debug-service/debug.service';
import { LoadingService } from '@finder/shared/services/loading-service/loading-service.service';
import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { TravelAgentService } from '@finder/shared/services/travel-agent/travel-agent.service';
import { FeatureDetectionService } from '@finder/shared/services/feature-detection/feature-detection.service';
import { FinderThemeService } from '@finder/shared/services/theme-service/finder-theme.service';
import { FinderPageKeyService } from '@finder/shared/services/finder-page-key/finder-page-key.service';
import { ContentNavigationService } from '@finder/shared/components/content-navigation/content-navigation.service';
import { RouteStateService } from '@finder/shared/services/route-state-service/route-state-service';
import { UrlParamsService } from '@finder/shared/services/url-params/url-params.service';
import { TokenInterceptor } from '@finder/shared/interceptors/token/token-interceptor.service';
import { LAZY_MODULES } from '@finder/shared/services/lazy-loader/tokens';
import { lazyArrayToObj } from '@finder/shared/services/lazy-loader/lazy-bundles';
import { ParkTimeService } from '@finder/shared/services/park-time/park-time.service';
import { SendURIInterceptor } from './shared/interceptors/send-uri/send-uri-interceptor.service';

/**
 * https://v17.angular.io/guide/standalone-components#using-standalone-components-in-ngmodule-based-applications
 *
 * To make Interceptors work you have to include withInterceptorsFromDI in provideHttpClient
 * https://stackoverflow.com/questions/77624853/interceptor-not-intercepting-in-angular-17
 *
 * Quick Lesson on Services
 * The @Injectable property determines where in the application it will load.
 * root: Automatically loaded at the top of the application. No need to use `providers` array
 * platform: Must be manually injected with `providers` array at the starting point of the component
 *           so it will trickle down to the children
 *
 * Service notes
 *
 * LoadingService
 * This service is in charge of updating the template once the underlying component is ready to be
 * displayed. It needs to be injected once at the top of the app and DI in the `constructor` and not
 * the `provider`.
 */
export const appConfig: ApplicationConfig = {
    providers: [
        // for the INTERCEPTORS
        LocaleService,
        HeaderOverridesService,
        // For all components
        UrlParamsService,
        RouteStateService,
        FinderAnalyticsService,
        TravelAgentService,
        FeatureDetectionService,
        LoadingService,
        FinderThemeService,
        FinderPageKeyService,
        ViewportDetectionService,
        SsrService,
        LoggerService,
        LogasaurusService,
        DebugService,
        ContentNavigationService,
        {
            provide: SEO_OPTIONS,
            useFactory: (config: ConfigService, ssrService: SsrService) => {
                /**
                 * Get a fresh copy of the SEO_OPTIONS instead of trying to reset changes
                 */
                let appSeoOptions = getSeoOptions();

                // appSeoOptions is aliased from the import since it now has the same name as the DI object
                if (config.getSiteId() === SITE_ID.hkdl) {
                    appSeoOptions.includeMetaTag.canonical = true;
                }

                // check for SSR
                if (ssrService.allowMetaData()) {
                    appSeoOptions.includeMetaTag.canonical = true;
                }

                return appSeoOptions;
            },
            deps: [ConfigService, SsrService],
        },
        WindowRefSeoHandler,
        SeoService,
        I18nService,
        SeoDataService,
        Subject,
        CorrelationService,
        // Provide service directly for DI
        CorrelationInterceptor,
        CookieService,
        DeviceDataService,
        // We have multiple WindowRef objects because each RA service want's their own ref. They can't share
        WindowRefErrorHandler,
        WindowRefLoggerHandler,
        ErrorService,
        GlobalExceptionHandlingService,
        HttpInterceptorService,
        CdnService,
        PageKeyServerDataService,
        PageKeyService,
        ParkTimeService,
        // import external modules
        importProvidersFrom(BrowserAnimationsModule),
        provideServiceWorker('/advanced-finder/web-workers/ngsw-worker.js', {
            //enabled: environment.production,
            enabled: false,
            scope: '/',
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000',
        }),
        provideRouter(APPLICATION_ROUTES),
        provideGeolocation(geoConfig),
        provideClientHydration(
            withIncrementalHydration(),
            withEventReplay()
        ),
        provideHttpClient(
            withFetch(),
            // This will use the existing provided interceptors
            withInterceptorsFromDi()
        ),
        {
            provide: UrlSerializer,
            useClass: CustomUrlSerializer
        },
        {
            provide: CORRELATION_CONFIG,
            useFactory: (config: ConfigService) => {
                // Need to call to ensure we have loaded the config data
                config.load();

                return {
                    wdprConversationCookieName: config.getValue('convIdCookieName'),
                    wdprConversationHeader: config.getValue('convIdHeaderName'),
                    wdprCorrelationHeader: config.getValue('corrIdHeaderName'),
                    wdprEndpointFilter: '/advanced-finder/log/?'
                };
            },
            deps: [ConfigService],
        },
        {
            provide: DeviceDetectionConfig,
            useValue: <DeviceDetectionOptions>{
                WDPR_DEVICE_DETECTION_COOKIE_NAME: 'WDPROView',
            },
        },
        provideAppInitializer(() => {
            const config = inject(ConfigService);
            const login = inject(RaAngularMyidLoginService);
            const ssr = inject(SsrService);

            return config.load().then((result: any) => {
                // ACME: configure the MyID login block ONCE (browser-only), before any
                // guard/resolver/component/interceptor uses it. redirectUri is derived from the
                // current origin so it works across brand domains and local ports.
                if (ssr.isBrowser() && config.getValue('acmeEnabled') === true) {
                    const cfg = config.getValue('myIdLoginConfig');
                    if (cfg?.authorizationUrl) {
                        cfg.redirectUri = `${window.location.origin}${cfg.localEndpoint}`;
                        login.setConfig(cfg);
                    }
                }

                return result;
            });
        }),
        provideAppInitializer(() => {
            const ssrService = inject(SsrService);
            const configService = inject(ConfigService);

            if (ssrService.isBrowser() && configService.getSiteId() !== SITE_ID.hkdl) {
                ClientIntegration.AngularInit.runDeferred({ feature: 'wdpr-content-advfinder-ui' })();
            }
        }),
        // Third-party block must be registered for DI; HTTP chain uses the SSR-aware wrapper.
        MyIdAuthInterceptor,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MyIdAuthSsrInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LocaleInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpInterceptorService,
            multi: true,
            deps: [ErrorService],
        },
        {
            provide: ErrorHandler,
            useClass: GlobalExceptionHandlingService
        },
        // Per https://disneyexperiences.atlassian.net/browse/GIT-33664, removing SPA override for T.com
        // in prepration for Small World
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TranslationInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SiteIdInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        },
        {
            provide: LOGASAURUS_URL,
            useValue: '/advanced-finder/log',
        },
        {
            provide: MAX_STRING_LENGTH,
            useValue: 1000
        },
        {
            provide: PAGE_KEY_URL_ENDPOINT,
            useValue: '/advanced-finder/page-key/',
        },
        {
            provide: PAGE_KEY_TEMPLATE,
            useFactory: (ssrService: SsrService) => {
                /**
                 * @SSR: if the page is being render on the server we have to provide
                 * the url the page is creating. the analytics library gets that data
                 * from the window object. since that doesn't exist on the server we
                 * have to provide it through an additional config in the `url` property.
                 * if we don't the `path` value is returned as the value for `PAGE_KEY_URL_ENDPOINT`.
                 * after `url` is populated in `PAGE_KEY_ADDITIONAL_CONFIG` we can return a new
                 * template that will assemble the correct value for the page-key to be created.
                 */
                return ssrService.isSSR() || ssrService.isDataFromTheServer() ? ['siteId', 'url'] : ['siteId', 'path'];
            },
            deps: [SsrService]
        },
        {
            provide: PAGE_KEY_ADDITIONAL_CONFIG,
            useFactory: (ssrService: SsrService, windowRef: LocalWindowRef) => {
                if (ssrService.isSSR() || ssrService.isDataFromTheServer()) {
                    return {
                        url: windowRef.nativeWindow.location.pathname
                    };
                }

                return {};
            },
            deps: [SsrService, LocalWindowRef]
        },
        // REMOVING P13N UNTIL WE MOVE TO ANGULAR 15, DUE TO DEPENDENCIES ISSUES
        // {
        //     provide: P13N_LOADER,
        //     useFactory: (finderAnalyticsService: FinderAnalyticsService) => () =>
        //         finderAnalyticsService.getGuestModel().toPromise(),
        //     deps: [FinderAnalyticsService],
        // },
        // {
        //     provide: P13N_DECISION_ATTRS_LOADER,
        //     useFactory: (finderAnalyticsService: FinderAnalyticsService) => () =>
        //         finderAnalyticsService.getDecisionAttributes(),
        //     deps: [FinderAnalyticsService],
        // },
        // {
        //     provide: P13N_BASE,
        //     useValue: {
        //         configuration: {
        //             CEA: {
        //                 decisionAttributesLoaderTimeout: 500,
        //             },
        //         },
        //     },
        // },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HeaderOverrideInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CustomDisneyHeadersInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SsrTokensInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SendURIInterceptor,
            multi: true
        },
        /**
         * This is how we lazy load the different pages. The Detail/Campaign/etc are processed here
         * Then loaded in src/app/shared/services/lazy-loader/lazy-loader-service.ts:lazyLoadComponent
         */
        {
            provide: LAZY_MODULES,
            useFactory: lazyArrayToObj
        },
        {
            provide: OneIdService,
            useFactory: OneIdFactory,
            deps: [LocalWindowRef, DebugLogService, SsrService],
        }
    ]
};
